import React from 'react';
import { cx } from '@/ui/utils/cx';

type DivProps = React.HTMLAttributes<HTMLDivElement> & {
  as?: 'div' | 'section' | 'main' | 'header' | 'footer' | 'article';
};

function shell(baseClass: string, { as: Tag = 'div', className, children, ...rest }: DivProps) {
  return (
    <Tag className={cx(baseClass, className)} {...rest}>
      {children}
    </Tag>
  );
}

/** Page shell — consistent outer padding and bottom scroll room */
export function PageContainer(props: DivProps) {
  return shell('ui-page-container admin-page', props);
}

/** Vertical section stack below page header */
export function ContentStack(props: DivProps) {
  return shell('ui-content-stack admin-page-sections', props);
}

/** Generic section wrapper */
export function Section(props: DivProps) {
  return shell('ui-section', props);
}

export function Card(props: DivProps) {
  return shell('ui-card card', props);
}

export function CardHeader(props: DivProps) {
  return shell('ui-card__header', props);
}

export function CardBody(props: DivProps) {
  return shell('ui-card__body', props);
}

export function CardFooter(props: DivProps) {
  return shell('ui-card__footer', props);
}

export function Panel(props: DivProps) {
  return shell('ui-panel', props);
}

export function Toolbar(props: DivProps) {
  return shell('ui-toolbar app-toolbar', props);
}

export function ToolbarGroup(props: DivProps) {
  return shell('ui-toolbar__group', props);
}

export function FormSection(props: DivProps) {
  return shell('ui-form-section', props);
}

export function FormGrid(props: DivProps) {
  return shell('ui-form-grid admin-form-grid', props);
}

export function FieldGroup(props: DivProps) {
  return shell('ui-field-group admin-form-field', props);
}

export function VerticalStack({
  gap = 'md',
  className,
  ...props
}: DivProps & { gap?: 'sm' | 'md' | 'lg' | 'xl' }) {
  return shell(`ui-stack ui-stack--vertical ui-stack--gap-${gap}`, { className, ...props });
}

export function HorizontalStack({
  gap = 'md',
  className,
  ...props
}: DivProps & { gap?: 'sm' | 'md' | 'lg' | 'xl' }) {
  return shell(`ui-stack ui-stack--horizontal ui-stack--gap-${gap}`, { className, ...props });
}

export function MetricGrid(props: DivProps) {
  return shell('ui-metric-grid metric-chips', props);
}

export function DashboardGrid(props: DivProps) {
  return shell('ui-dashboard-grid dashboard-worker-grid', props);
}

export function ResponsiveGrid(props: DivProps) {
  return shell('ui-responsive-grid', props);
}

export function SplitLayout(props: DivProps) {
  return shell('ui-split-layout', props);
}

export function ScrollableContainer(props: DivProps) {
  return shell('ui-scrollable', props);
}

export function AccordionSection(props: DivProps) {
  return shell('ui-accordion-section', props);
}

export function PageHeaderLayout(props: DivProps) {
  return shell('ui-page-header page-header', props);
}

export function TabPanel(props: DivProps) {
  return shell('ui-tab-panel', props);
}
